// The thirteen checks (front/RULES.md), built from what the API records today: the outcome code, the
// discrepancies and the notes. When the API's own per-check list arrives
// it is used as it is.
import type { Check, Discrepancy, Fix, Invoice, InvoiceDetail } from "../api/types";
import { amount, signed } from "./format";
import type { CheckResult } from "./components";

export const CHECKS = ["Is an invoice", "Invoice #, date & total found", "Invoice adds up", "PO found", "Supplier matches", "Billed to you", "PO is open", "Not a duplicate", "PO adds up", "Line items", "Freight", "Tax", "Total"] as const;
export type CheckName = (typeof CHECKS)[number];

/** Not one of the thirteen: a hold on an invoice that passed them all, set by the amount limit on Settings → Approving. */
export const OVER_LIMIT = "Over your limit";

export const CHECK_DESCRIPTIONS: Record<CheckName, string> = {
  "Is an invoice": "not a statement or credit memo",
  "Invoice #, date & total found": "all three read from the PDF",
  "Invoice adds up": "lines + freight + tax = total",
  "PO found": "from the PO number printed",
  "Supplier matches": "the PO’s supplier sent it",
  "Billed to you": "addressed to your company",
  "PO is open": "not already approved",
  "Not a duplicate": "invoice # not approved before",
  "PO adds up": "its lines total the PO",
  "Line items": "every line on both sides",
  Freight: "within your tolerance",
  Tax: "equals the PO’s tax",
  Total: "equals the PO total",
};

export interface CheckView {
  n: CheckName | string;
  r: CheckResult;
  /** The figures or reason, e.g. "64.68 vs PO 36.85 (+27.83) – allowed ±3.69". */
  d: string;
  /** The short form for compact lists. */
  s: string;
}

/** Which check an outcome code or discrepancy code fails. "Couldn’t process" is the technical bucket. */
export function checkForCode(code: string | null | undefined): CheckName | "Couldn’t process" | typeof OVER_LIMIT | null {
  if (!code) return null;
  if (code === "VOUCHED" || code === "AWAITING_RELEASE") return null;
  if (code === "OVER_LIMIT") return OVER_LIMIT;
  if (code.startsWith("LINE_")) return "Line items";
  switch (code) {
    case "FREIGHT": case "FREIGHT_UPDATE_CHECK": return "Freight";
    case "TAX": return "Tax";
    case "TOTAL": return "Total";
    case "INVOICE_NUMBER_MISSING": case "INVOICE_DATE_MISSING": case "INVOICE_TOTAL_MISSING": return "Invoice #, date & total found";
    case "INVOICE_ARITHMETIC": return "Invoice adds up";
    case "PO_STRUCTURE": return "PO adds up";
    case "NOT_AN_INVOICE": return "Is an invoice";
    case "NO_PO_REFERENCE": case "PO_REFERENCE_FORMAT": case "MULTIPLE_PO_REFERENCES": case "PO_NOT_FOUND": return "PO found";
    case "PO_NOT_OPEN": return "PO is open";
    case "ALREADY_VOUCHED": case "DUPLICATE_INVOICE": return "Not a duplicate";
    case "SUPPLIER_MISMATCH": case "SUPPLIER_UNKNOWN": return "Supplier matches";
    case "BILL_TO_MISMATCH": return "Billed to you";
    default: return "Couldn’t process";
  }
}

/**
 * A check named as the thing being checked reads right beside a green tick and wrong in red ("PO is open" when the PO
 * is closed), so wherever a check is shown as failed it takes this wording.
 */
export const FAILED_WORDING: Record<CheckName | "Couldn’t process" | typeof OVER_LIMIT, string> = {
  "Is an invoice": "Not an invoice",
  "Invoice #, date & total found": "Invoice #, date or total missing",
  "Invoice adds up": "Invoice doesn’t add up",
  "PO found": "PO not found",
  "Supplier matches": "Supplier doesn’t match",
  "Billed to you": "Not billed to you",
  "PO is open": "PO not open",
  "Not a duplicate": "Duplicate invoice",
  "PO adds up": "PO doesn’t add up",
  "Line items": "Line items differ",
  Freight: "Freight differs",
  Tax: "Tax differs",
  Total: "Total differs",
  "Couldn’t process": "Couldn’t process",
  [OVER_LIMIT]: "Over your limit",
};

export function failedWording(check: string): string {
  return (FAILED_WORDING as Record<string, string>)[check] ?? check;
}

/** The group a Needs-you invoice belongs to, from its list row alone. */
export function failedCheckOf(invoice: Pick<Invoice, "code" | "status">): string {
  return checkForCode(invoice.code) ?? (invoice.status === "Failed" ? "Couldn’t process" : "Line items");
}

/** Why Syncore couldn't be read, in plain words (the API's sentence names secrets and tenants). */
export function plainSyncoreError(message: string | null | undefined): string {
  const m = message ?? "";
  if (/not set|no Syncore Email|credentials missing/i.test(m)) return "Syncore isn’t connected yet – add the API key in Settings → Syncore";
  if (/no longer on the job|not on the job/i.test(m)) return "The PO is no longer on the job in Syncore";
  if (/rejected|401|403|unauthori|forbidden/i.test(m)) return "Syncore rejected the API key – check it in Settings → Syncore";
  if (/timed out|timeout|not responding|unavailable|50\d|refused/i.test(m)) return "Syncore didn’t respond – try again in a minute";
  if (!m) return "Syncore couldn’t be read";
  return "Syncore couldn’t be read";
}

/** What an invoice's row says went wrong: the difference for a check that failed; plain words, never the API's sentence, for a technical stop. */
export function plainSummary(invoice: Pick<Invoice, "code" | "codeTitle" | "summary" | "status">): string {
  if (!isTechnical(invoice)) return invoice.summary ?? invoice.codeTitle ?? "";
  switch (invoice.code) {
    case "MISSING_CREDENTIALS": return /attach|email/i.test(invoice.summary ?? "") ? "The Syncore sign-in for attaching isn’t set up – Settings → Syncore" : "Syncore isn’t connected – add the API key in Settings → Syncore";
    case "SYNCORE_LOGIN_FAILED": return "Syncore rejected the sign-in – check it in Settings → Syncore";
    case "RETRIES_EXHAUSTED": return "Syncore didn’t respond after every try";
    case "SYNCORE_RESPONSE": return "Syncore answered in a way AutoVouch couldn’t read";
    case "SYNCORE_UI_CHANGED": return "Syncore’s pages changed – AutoVouch support has been told";
    case "TENANT_PAUSED": return "Paused by AutoVouch support";
    case "SPEND_CAP_REACHED": return "This month’s reading budget is used up";
    case "DEAD_LETTERED": return "Taken out of the queue by AutoVouch staff";
    default:
      if (invoice.code?.startsWith("SYNCORE_API")) return "Syncore’s API refused the request";
      if (invoice.code?.startsWith("CLAUDE") || invoice.code?.startsWith("EXTRACTION")) return "The PDF couldn’t be read";
      return invoice.codeTitle ?? "Couldn’t process";
  }
}

/** True when the failure is technical (nothing to fix in Syncore, only Check again). */
export function isTechnical(invoice: Pick<Invoice, "code">): boolean {
  return checkForCode(invoice.code) === "Couldn’t process";
}

const ORDER: Record<CheckResult, number> = { no: 0, adj: 1, ok: 2, na: 3 };

export function sortChecks(list: CheckView[]): CheckView[] {
  return list.slice().sort((a, b) => ORDER[a.r] - ORDER[b.r]);
}

export function countChecks(list: CheckView[], r: CheckResult): number {
  return list.filter((c) => c.r === r).length;
}

function fromApiCheck(c: Check): CheckView {
  const r: CheckResult = c.result === "Passed" ? "ok" : c.result === "Adjusted" ? "adj" : c.result === "Failed" ? "no" : "na";
  let d = c.detail ?? "";
  if (!d && c.invoice != null && c.po != null) {
    d = `${fmt(c.invoice)} vs PO ${fmt(c.po)}` + (c.difference != null ? ` (${fmtDiff(c.difference)})` : "") + (c.allowed != null ? ` – allowed ±${fmt(c.allowed)}` : "");
  }
  return { n: c.name, r, d, s: c.detail ?? c.name };
}

function fmt(v: number | string): string {
  return typeof v === "number" ? amount(v) : v;
}

function fmtDiff(v: number | string): string {
  return typeof v === "number" ? signed(v) : v;
}

/** The thirteen checks for an invoice detail. */
export function checksOf(detail: InvoiceDetail): CheckView[] {
  if (detail.checks && detail.checks.length) return detail.checks.map(fromApiCheck);

  const inv = detail.invoice;
  const result: Record<string, CheckView> = {};
  for (const n of CHECKS) result[n] = { n, r: "ok", d: "", s: "" };

  const names: string[] = [...CHECKS];
  if (inv.code === "OVER_LIMIT" || detail.limitWaived) { names.push(OVER_LIMIT); result[OVER_LIMIT] = { n: OVER_LIMIT, r: detail.limitWaived ? "adj" : "ok", d: detail.limitWaived ? "Approved over the limit by an approver" : "", s: "" }; }
  const fail = (n: string, d: string, s?: string) => { result[n] = { n, r: "no", d, s: s ?? d }; };
  const adjust = (n: CheckName, d: string, s?: string) => { if (result[n].r === "ok") result[n] = { n, r: "adj", d, s: s ?? d }; };
  const notChecked = (from: CheckName, why: string) => {
    const i = CHECKS.indexOf(from);
    for (const n of CHECKS.slice(i + 1)) if (result[n].r === "ok") result[n] = { n, r: "na", d: why, s: why };
  };

  const code = inv.code;
  const failedCheck = checkForCode(code);
  const summary = inv.summary ?? detail.outcome?.title ?? "";

  // Nothing read yet, or a technical stop: only what happened is known.
  if (inv.status === "Queued" || inv.status === "Processing" || (inv.status === "Held" && !inv.awaitingRelease)) {
    for (const n of CHECKS) result[n] = { n, r: "na", d: "not checked yet", s: "" };
    return CHECKS.map((n) => result[n]);
  }

  if (failedCheck === "Couldn’t process") {
    const readOk = !!detail.extraction;
    for (const n of CHECKS) result[n] = { n, r: readOk && (n === "Is an invoice" || n === "Invoice #, date & total found" || n === "Invoice adds up") ? "ok" : "na", d: readOk ? "" : "not reached", s: "" };
    return CHECKS.map((n) => result[n]);
  }

  // Discrepancies, from the matcher: each names the check it failed and carries the figures.
  for (const d of detail.discrepancies ?? []) {
    const n = checkForCode(d.code);
    if (!n || n === "Couldn’t process") continue;
    if (d.severity === "Review" || d.severity === "Fail" || inv.status !== "Vouched" || n === OVER_LIMIT) fail(n, d.message, shortOf(d));
    else adjust(n, d.message, shortOf(d));
  }

  if (failedCheck && result[failedCheck].r !== "no") {
    fail(failedCheck, summary);
  }

  // The outcome code says where checking stopped.
  switch (failedCheck) {
    case "Is an invoice": notChecked("Is an invoice", "not checked: not an invoice"); break;
    case "Invoice #, date & total found": if (code === "INVOICE_TOTAL_MISSING") notChecked("Invoice #, date & total found", "not checked: no total to compare"); break;
    case "Invoice adds up": notChecked("Invoice adds up", "not checked: the invoice doesn’t add up"); break;
    case "PO found": notChecked("PO found", "not checked: no PO to compare"); break;
    case "Supplier matches": case "Billed to you": case "PO is open": case "Not a duplicate": notChecked(failedCheck, "not checked: stopped at " + failedCheck.toLowerCase()); break;
    case "PO adds up": notChecked("PO adds up", "not compared: the PO doesn’t add up"); break;
  }

  // Concessions and adjustments: passed, but by a rule.
  if (detail.freightAdjustment) {
    const f = detail.freightAdjustment;
    const diff = f.invoiceFreight - f.poFreight;
    const why = f.reason === "Blank" ? "no freight on the PO, filled in" : f.reason === "Lower" ? "lower than the PO’s, accepted" : `allowed ±${amount(f.allowedDifference)}`;
    if (result.Freight.r === "ok") adjust("Freight", `${amount(f.invoiceFreight)} vs PO ${amount(f.poFreight)} (${signed(diff)}) – ${why}`, `Freight ${signed(diff)} adjusted`);
  }
  if (detail.linesAdjustment) {
    const l = detail.linesAdjustment;
    const diff = l.invoiceLines - l.poLines;
    if (result["Line items"].r === "ok") adjust("Line items", `${amount(l.invoiceLines)} vs PO ${amount(l.poLines)} (${signed(diff)}) – ${l.onPo ? "Invoice adjustment line already on the PO" : `allowed ±${amount(l.allowedDifference)}`}`, `Lines ${signed(diff)} adjusted`);
  }
  for (const note of detail.notes ?? []) {
    const lower = note.toLowerCase();
    if (lower.includes("tax")) adjust("Tax", note, "Tax explained by a PO line");
    else if (lower.includes("freight")) adjust("Freight", note, "Freight explained by a PO line");
    else if (lower.includes("po") && (lower.includes("chosen") || lower.includes("supplier") || lower.includes("hyphen") || lower.includes("job"))) adjust("PO found", note, note);
    else if (lower.includes("supplier") || lower.includes("name")) adjust("Supplier matches", note, note);
    else adjust("Line items", note, note);
  }

  return names.map((n) => result[n]);
}

function shortOf(d: Discrepancy): string {
  const n = checkForCode(d.code);
  if (d.delta != null && n && n !== "Couldn’t process") return `${n} ${signed(d.delta)}`;
  return d.message.length > 48 ? d.message.slice(0, 46) + "…" : d.message;
}

/** Where and what to fix in Syncore, derived from the detail when the API doesn't say. */
export function fixOf(detail: InvoiceDetail): Fix | null {
  if (detail.fix !== undefined) return detail.fix;
  const inv = detail.invoice;
  if (inv.status !== "NeedsReview" && inv.status !== "Failed") return null;
  const po = inv.jobNumber && inv.poNumber ? `${inv.jobNumber}-${inv.poNumber}` : null;
  const code = inv.code ?? "";
  const first = (detail.discrepancies ?? []).find((d) => checkForCode(d.code) === checkForCode(code)) ?? (detail.discrepancies ?? [])[0];
  switch (checkForCode(code)) {
    case "Freight":
      return { kind: "Field", po, field: "Freight", current: detail.poTotals?.freight != null ? amount(detail.poTotals.freight) : null, expected: detail.invoiceTotals?.freight != null ? amount(detail.invoiceTotals.freight) : null, autoFixable: false };
    case "Tax":
      return { kind: "Field", po, field: "Tax", current: detail.poTotals?.tax != null ? amount(detail.poTotals.tax) : null, expected: detail.invoiceTotals?.tax != null ? amount(detail.invoiceTotals.tax) : null, autoFixable: false };
    case "Line items":
      return { kind: code === "LINE_NOT_ON_PO" ? "AddLine" : "Field", po, field: code === "LINE_NOT_ON_PO" ? "Add line" : "PO lines", current: null, expected: first?.message ?? null, autoFixable: false };
    case "Total":
      return { kind: "Field", po, field: "Total", current: detail.poTotals?.total != null ? amount(detail.poTotals.total) : null, expected: detail.invoiceTotals?.total != null ? amount(detail.invoiceTotals.total) : null, autoFixable: false };
    case "PO adds up":
      return { kind: "Field", po, field: "PO lines", current: null, expected: first?.message ?? "lines + freight ≠ total", autoFixable: false };
    case "Invoice #, date & total found":
      return { kind: "Manual", po, field: "Approve PO", current: null, expected: null, autoFixable: false };
    case "PO found":
      return { kind: "Manual", po, field: "PO number", current: detail.printedPo, expected: null, autoFixable: false };
    case OVER_LIMIT:
      return { kind: "Approve", po, field: "Approve now", current: detail.invoiceTotals?.total != null ? amount(detail.invoiceTotals.total) : null, expected: null, autoFixable: false };
    case "Couldn’t process":
      return null;
    default:
      return { kind: "Manual", po, field: detail.outcome?.title ?? "Approve PO", current: null, expected: null, autoFixable: false };
  }
}

/** How each failed check is fixed: in Syncore, by Check again, or by Update PO freight. */
export interface GroupConfig {
  auto?: boolean;
  hand?: boolean;
  tech?: boolean;
  /** Nothing to fix: every check passed and a person approves it. */
  approve?: boolean;
  fix: string;
  columns: [string, (i: Invoice) => string][];
}

export const GROUPS: Record<string, GroupConfig> = {
  [OVER_LIMIT]: { approve: true, fix: "Approve now", columns: [["Total", (i) => plainSummary(i)]] },
  Freight: { auto: true, fix: "PO freight → invoice", columns: [["What differs", (i) => plainSummary(i)]] },
  Tax: { auto: true, fix: "PO tax → invoice", columns: [["What differs", (i) => plainSummary(i)]] },
  "Line items": { fix: "PO lines", columns: [["What differs", (i) => plainSummary(i)]] },
  Total: { fix: "PO total", columns: [["What differs", (i) => plainSummary(i)]] },
  "Invoice #, date & total found": { fix: "Fix in Syncore", hand: true, columns: [["Not read", (i) => plainSummary(i)]] },
  "PO adds up": { fix: "PO lines", columns: [["PO lines vs total", (i) => plainSummary(i)]] },
  "Invoice adds up": { fix: "Fix in Syncore", hand: true, columns: [["What happened", (i) => plainSummary(i)]] },
  "Couldn’t process": { fix: "Check again", tech: true, columns: [["What happened", (i) => plainSummary(i)]] },
  "PO found": { fix: "Fix in Syncore", hand: true, columns: [["PO number printed", (i) => plainSummary(i)]] },
  "Supplier matches": { fix: "Fix in Syncore", hand: true, columns: [["Supplier", (i) => plainSummary(i)]] },
  "Billed to you": { fix: "Add the name in Settings", hand: true, columns: [["Billed to", (i) => plainSummary(i)]] },
  "PO is open": { fix: "Fix in Syncore", hand: true, columns: [["PO", (i) => plainSummary(i)]] },
  "Not a duplicate": { fix: "Fix in Syncore", hand: true, columns: [["Why", (i) => plainSummary(i)]] },
  "Is an invoice": { fix: "Fix in Syncore", hand: true, columns: [["Why", (i) => plainSummary(i)]] },
};

export function groupConfig(check: string): GroupConfig {
  return GROUPS[check] ?? { fix: "Fix in Syncore", hand: true, columns: [["What happened", (i) => plainSummary(i)]] };
}

export interface NeedsGroup {
  check: string;
  items: Invoice[];
  ids: string[];
  oldest: Invoice;
  cfg: GroupConfig;
}

export function groupNeeds(list: Invoice[]): NeedsGroup[] {
  const m = new Map<string, Invoice[]>();
  for (const i of list) {
    const k = failedCheckOf(i);
    const arr = m.get(k) ?? [];
    arr.push(i);
    m.set(k, arr);
  }
  return [...m.entries()]
    .map(([check, items]) => {
      const sorted = items.slice().sort((a, b) => a.receivedAt.localeCompare(b.receivedAt));
      return { check, items: sorted, ids: sorted.map((i) => i.id), oldest: sorted[0], cfg: groupConfig(check) };
    })
    .sort((a, b) => b.items.length - a.items.length || a.oldest.receivedAt.localeCompare(b.oldest.receivedAt));
}
